/**
 * Match Results Component
 * Displays asset recognition results with confidence scores
 */

import {
	CheckCircleOutlined,
	EyeOutlined,
	InfoCircleOutlined,
	ReloadOutlined,
	WarningOutlined,
} from "@ant-design/icons";
import {
	Badge,
	Button,
	Card,
	Col,
	Divider,
	Row,
	Space,
	Tag,
	Typography,
} from "antd";
import React from "react";
import { useNavigate } from "react-router-dom";
import type {
	AssetMatch,
	AssetRecognitionResponse,
} from "../../../../models/assets/asset-recognition.model";

const { Title, Text, Paragraph } = Typography;

interface MatchResultsProps {
	result: AssetRecognitionResponse;
	onReset: () => void;
	capturedPhotoUrl: string;
}

export const MatchResults: React.FC<MatchResultsProps> = ({
	result,
	onReset,
	capturedPhotoUrl,
}) => {
	const navigate = useNavigate();

	const getConfidenceLevel = (
		confidence: number,
	): "high" | "medium" | "low" => {
		if (confidence >= 0.8) return "high";
		if (confidence >= 0.6) return "medium";
		return "low";
	};

	const getConfidenceBadge = (confidence: number) => {
		const level = getConfidenceLevel(confidence);
		const percentage = (confidence * 100).toFixed(0);

		const config = {
			high: { color: "success", icon: <CheckCircleOutlined /> },
			medium: { color: "warning", icon: <WarningOutlined /> },
			low: { color: "default", icon: <InfoCircleOutlined /> },
		};

		return (
			<Tag color={config[level].color} icon={config[level].icon}>
				{percentage}% Match
			</Tag>
		);
	};

	const handleViewAsset = (assetId: number) => {
		// Navigate to asset detail page
		navigate(`/assets`, { state: { selectedAssetId: assetId } });
	};

	const renderMatchedFields = (match: AssetMatch) => {
		const fields = [];
		if (match.matchedFields.serialNumber) fields.push("Serial Number");
		if (match.matchedFields.assetTag) fields.push("Asset Tag");
		if (match.matchedFields.modelNumber) fields.push("Model Number");
		if (match.matchedFields.manufacturer) fields.push("Manufacturer");
		if (match.matchedFields.assetType) fields.push("Asset Type");

		return fields.length > 0 ? (
			<div style={{ marginTop: "8px" }}>
				<Text type="secondary" style={{ fontSize: "12px" }}>
					Matched: {fields.join(", ")}
				</Text>
			</div>
		) : null;
	};

	const renderExtractedData = () => {
		const { extractedData } = result;
		const hasData = Object.keys(extractedData).length > 0;

		if (!hasData) return null;

		return (
			<Card
				size="small"
				title="Extracted Information"
				style={{ marginTop: "16px" }}
			>
				<Space direction="vertical" style={{ width: "100%" }}>
					{extractedData.serialNumber && (
						<Row justify="space-between">
							<Col>
								<Text strong>Serial Number:</Text>
							</Col>
							<Col>
								<Text>{extractedData.serialNumber.text}</Text>
								<Text
									type="secondary"
									style={{ marginLeft: "8px", fontSize: "12px" }}
								>
									({(extractedData.serialNumber.confidence * 100).toFixed(0)}%)
								</Text>
							</Col>
						</Row>
					)}
					{extractedData.assetTag && (
						<Row justify="space-between">
							<Col>
								<Text strong>Asset Tag:</Text>
							</Col>
							<Col>
								<Text>{extractedData.assetTag.text}</Text>
								<Text
									type="secondary"
									style={{ marginLeft: "8px", fontSize: "12px" }}
								>
									({(extractedData.assetTag.confidence * 100).toFixed(0)}%)
								</Text>
							</Col>
						</Row>
					)}
					{extractedData.modelNumber && (
						<Row justify="space-between">
							<Col>
								<Text strong>Model Number:</Text>
							</Col>
							<Col>
								<Text>{extractedData.modelNumber.text}</Text>
								<Text
									type="secondary"
									style={{ marginLeft: "8px", fontSize: "12px" }}
								>
									({(extractedData.modelNumber.confidence * 100).toFixed(0)}%)
								</Text>
							</Col>
						</Row>
					)}
					{extractedData.manufacturer && (
						<Row justify="space-between">
							<Col>
								<Text strong>Manufacturer:</Text>
							</Col>
							<Col>
								<Text>{extractedData.manufacturer.text}</Text>
								<Text
									type="secondary"
									style={{ marginLeft: "8px", fontSize: "12px" }}
								>
									({(extractedData.manufacturer.confidence * 100).toFixed(0)}%)
								</Text>
							</Col>
						</Row>
					)}
					{extractedData.assetType && (
						<Row justify="space-between">
							<Col>
								<Text strong>Asset Type:</Text>
							</Col>
							<Col>
								<Text>{extractedData.assetType.text}</Text>
								<Text
									type="secondary"
									style={{ marginLeft: "8px", fontSize: "12px" }}
								>
									({(extractedData.assetType.confidence * 100).toFixed(0)}%)
								</Text>
							</Col>
						</Row>
					)}
				</Space>
			</Card>
		);
	};

	return (
		<div className="match-results-container">
			<Card>
				<Space direction="vertical" size="large" style={{ width: "100%" }}>
					{/* Summary */}
					<div style={{ textAlign: "center" }}>
						<Title level={4}>
							{result.matches.length === 0 ? (
								<>
									<InfoCircleOutlined style={{ color: "#faad14" }} /> No Matches
									Found
								</>
							) : result.matches.length === 1 ? (
								<>
									<CheckCircleOutlined style={{ color: "#52c41a" }} /> Asset
									Identified
								</>
							) : (
								<>
									<InfoCircleOutlined style={{ color: "#1890ff" }} /> Multiple
									Potential Matches
								</>
							)}
						</Title>
						<Text type="secondary">
							Searched {result.assetsSearched} assets within{" "}
							{result.searchRadiusKm}km
						</Text>
					</div>

					<Divider />

					{/* No matches message */}
					{result.matches.length === 0 && (
						<Card
							size="small"
							style={{ background: "#fffbe6", border: "1px solid #ffe58f" }}
						>
							<Paragraph>
								<WarningOutlined style={{ color: "#faad14" }} /> No matching
								assets were found in your area.
							</Paragraph>
							<Paragraph style={{ marginBottom: 0 }}>
								Try:
								<ul>
									<li>Getting closer to the asset</li>
									<li>Taking a clearer photo of identification tags</li>
									<li>Ensuring the asset is registered in the system</li>
								</ul>
							</Paragraph>
						</Card>
					)}

					{/* Match results */}
					{result.matches.map((match, index) => {
						const confidenceLevel = getConfidenceLevel(match.confidence);

						return (
							<Card
								key={index}
								size="small"
								className={`match-item ${confidenceLevel}-confidence`}
								hoverable
								onClick={() => handleViewAsset(match.asset.id)}
							>
								<Space direction="vertical" style={{ width: "100%" }}>
									<Row justify="space-between" align="middle">
										<Col>
											<Space>
												{index === 0 && <Badge status="processing" />}
												<Text strong style={{ fontSize: "16px" }}>
													{match.asset.assetType?.name || "Unknown Type"}
												</Text>
											</Space>
										</Col>
										<Col>{getConfidenceBadge(match.confidence)}</Col>
									</Row>

									<Row gutter={[16, 8]}>
										<Col span={12}>
											<Text type="secondary">Location:</Text>
											<div>
												<Text>{match.asset.location?.name || "N/A"}</Text>
											</div>
										</Col>
										<Col span={12}>
											<Text type="secondary">Manufacturer:</Text>
											<div>
												<Text>{match.asset.manufacturer?.name || "N/A"}</Text>
											</div>
										</Col>
										<Col span={12}>
											<Text type="secondary">Serial Number:</Text>
											<div>
												<Text>{match.asset.serialNumber || "N/A"}</Text>
											</div>
										</Col>
										<Col span={12}>
											<Text type="secondary">Asset Tag:</Text>
											<div>
												<Text>{match.asset.assetTag || "N/A"}</Text>
											</div>
										</Col>
									</Row>

									{renderMatchedFields(match)}

									<Button
										type="primary"
										icon={<EyeOutlined />}
										onClick={(e) => {
											e.stopPropagation();
											handleViewAsset(match.asset.id);
										}}
										block
									>
										View Asset Details
									</Button>
								</Space>
							</Card>
						);
					})}

					{/* Extracted data */}
					{renderExtractedData()}

					{/* Actions */}
					<Divider />
					<Button
						icon={<ReloadOutlined />}
						onClick={onReset}
						size="large"
						block
					>
						Scan Another Asset
					</Button>
				</Space>
			</Card>
		</div>
	);
};
